import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import './Jobs.css';
import JobPostingPreview from '../Recruiter/JobPosting/JobPostingPreview';
import {
  Search,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  SlidersHorizontal,
  Building2,
  Calendar,
  Clock,
  IndianRupee,
  Filter,
  Loader2,
  X,
  Users,
  ExternalLink,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';

Modal.setAppElement('#root');

const ITEMS_PER_PAGE = 10;

const Jobs = ({ onViewApplicants }) => {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [collegeUserId] = useState(localStorage.getItem('userId'));

  // Filters & Search
  const [statusFilter, setStatusFilter] = useState('Open');
  const [typeFilter, setTypeFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  // Sorting
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Modal
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  /* ── Utility Functions ───────────────────────────────────── */
  const getJobStatus = (applyByDate, applyByTime) => {
    if (!applyByDate) return 'Open';
    const applyDateTimeString = `${applyByDate}T${applyByTime || '23:59:59'}`;
    try {
      const applyDateTime = new Date(applyDateTimeString);
      const now = new Date();
      if (isNaN(applyDateTime.getTime())) return 'Open';
      return now < applyDateTime ? 'Open' : 'Closed';
    } catch {
      return 'Open';
    }
  };

  const formatApplyBy = (applyByDate, applyByTime) => {
    if (!applyByDate) return 'N/A';
    try {
      const date = new Date(applyByDate);
      const options = { day: '2-digit', month: 'short', year: 'numeric' };
      const formattedDate = date.toLocaleDateString('en-GB', options);
      let formattedTime = '';
      if (applyByTime) formattedTime = ` by ${applyByTime}`;
      return `${formattedDate}${formattedTime}`;
    } catch {
      return 'Invalid Date';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const parseCTC = (ctcValue) => {
    if (!ctcValue) return 0;
    const str = String(ctcValue).replace(/[^\d.]/g, '');
    return parseFloat(str) || 0;
  };

  /* ── Fetch Jobs ──────────────────────────────────────────── */
  useEffect(() => {
    const fetchPostedJobs = async () => {
      if (!collegeUserId) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/college/posted-jobs/${collegeUserId}`
        );
        setJobs(response.data);
      } catch (error) {
        console.error('Failed to fetch jobs:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPostedJobs();
  }, [collegeUserId]);

  // Reset page on filter/search/sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, typeFilter, sortField, sortDir]);

  /* ── Sort Handler ────────────────────────────────────────── */
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  /* ── Get Unique Job Types ────────────────────────────────── */
  const jobTypes = useMemo(() => {
    const types = new Set(jobs.map((j) => j.typeOfWork).filter(Boolean));
    return ['All', ...Array.from(types)];
  }, [jobs]);

  /* ── Processed (filtered + sorted) List ──────────────────── */
  const processed = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();

    let list = jobs
      .filter((job) => getJobStatus(job.applyByDate, job.applyByTime) === statusFilter)
      .filter((job) => typeFilter === 'All' || job.typeOfWork === typeFilter)
      .filter((job) => {
        if (!q) return true;
        return (
          (job.companyName || '').toLowerCase().includes(q) ||
          (job.role || '').toLowerCase().includes(q) ||
          (job.typeOfWork || '').toLowerCase().includes(q) ||
          (job.ctc || '').toLowerCase().includes(q)
        );
      });

    if (sortField) {
      list = [...list].sort((a, b) => {
        if (sortField === 'ctc') {
          const aV = parseCTC(a.ctc);
          const bV = parseCTC(b.ctc);
          return sortDir === 'asc' ? aV - bV : bV - aV;
        }
        if (sortField === 'company') {
          const aV = (a.companyName || '').toLowerCase();
          const bV = (b.companyName || '').toLowerCase();
          return sortDir === 'asc' ? aV.localeCompare(bV) : bV.localeCompare(aV);
        }
        if (sortField === 'applyBy') {
          const aV = a.applyByDate ? new Date(a.applyByDate).getTime() : 0;
          const bV = b.applyByDate ? new Date(b.applyByDate).getTime() : 0;
          return sortDir === 'asc' ? aV - bV : bV - aV;
        }
        if (sortField === 'dateOfVisit') {
          const aV = a.dateOfVisit ? new Date(a.dateOfVisit).getTime() : 0;
          const bV = b.dateOfVisit ? new Date(b.dateOfVisit).getTime() : 0;
          return sortDir === 'asc' ? aV - bV : bV - aV;
        }
        return 0;
      });
    }

    return list;
  }, [jobs, searchTerm, statusFilter, typeFilter, sortField, sortDir]);

  /* ── Pagination ──────────────────────────────────────────── */
  const totalPages = Math.ceil(processed.length / ITEMS_PER_PAGE);
  const paginated = processed.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const getPageNums = () => {
    const pages = [];
    const max = 5;
    let start = Math.max(1, currentPage - Math.floor(max / 2));
    let end = Math.min(totalPages, start + max - 1);
    if (end - start < max - 1) start = Math.max(1, end - max + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  /* ── Sort Icon ───────────────────────────────────────────── */
  const SortIcon = ({ field }) => {
    const active = sortField === field;
    return (
      <span className="jb-sort-icons">
        <ChevronUp size={11} className={active && sortDir === 'asc' ? 'jb-si-on' : ''} />
        <ChevronDown size={11} className={active && sortDir === 'desc' ? 'jb-si-on' : ''} />
      </span>
    );
  };

  /* ── Modal Handlers ──────────────────────────────────────── */
  const openModal = (job) => {
    setSelectedJob(job);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedJob(null);
  };

  const handleViewStudents = (jobId, jobRole) => {
    closeModal();
    onViewApplicants({ jobId, jobRole });
  };

  /* ── Loading ─────────────────────────────────────────────── */
  if (isLoading) {
    return (
      <div className="jb-root">
        <div className="jb-loading">
          <div className="jb-loading-card">
            <Loader2 className="jb-spin" size={36} />
            <span>Loading jobs…</span>
          </div>
        </div>
      </div>
    );
  }

  /* ── Render ──────────────────────────────────────────────── */
  return (
    <div className="jb-root">
      {/* Background blobs */}
      <div className="jb-bg-blob jb-blob-1" />
      <div className="jb-bg-blob jb-blob-2" />

      {/* ── Header ── */}
      <div className="jb-header">
        <div className="jb-header-left">
          <div className="jb-title-icon">
            <Briefcase size={26} />
          </div>
          <div>
            <h1 className="jb-title">Job Postings</h1>
            <p className="jb-subtitle">
              {localStorage.getItem('collegeName') || 'Your College'}
            </p>
          </div>
        </div>
        <span className="jb-count-badge">
          <Briefcase size={14} />
          {processed.length} job{processed.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Controls ── */}
      <div className="jb-controls">
        {/* Search */}
        <div className="jb-search-wrap">
          <Search size={16} className="jb-search-icon" />
          <input
            className="jb-search"
            type="text"
            placeholder="Search company, role, type…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="jb-clear-btn" onClick={() => setSearchTerm('')}>
              <X size={12} />
            </button>
          )}
        </div>

        {/* Status Filters */}
        <div className="jb-filter-group">
          <span className="jb-filter-label">
            <Filter size={13} /> Status
          </span>
          <button
            className={`jb-filter-btn ${statusFilter === 'Open' ? 'active jb-open' : ''}`}
            onClick={() => setStatusFilter('Open')}
          >
            Open
          </button>
          <button
            className={`jb-filter-btn ${statusFilter === 'Closed' ? 'active jb-closed' : ''}`}
            onClick={() => setStatusFilter('Closed')}
          >
            Closed
          </button>
        </div>

        {/* Type Filter */}
        {jobTypes.length > 2 && (
          <div className="jb-filter-group">
            <span className="jb-filter-label">
              <SlidersHorizontal size={13} /> Type
            </span>
            {jobTypes.map((type) => (
              <button
                key={type}
                className={`jb-filter-btn ${typeFilter === type ? 'active jb-type' : ''}`}
                onClick={() => setTypeFilter(type)}
              >
                {type}
              </button>
            ))}
          </div>
        )}

        {/* Sort Buttons */}
        <div className="jb-filter-group">
          <span className="jb-filter-label">
            <SlidersHorizontal size={13} /> Sort
          </span>
          <button
            className={`jb-sort-btn ${sortField === 'ctc' ? 'active' : ''}`}
            onClick={() => handleSort('ctc')}
            title={
              sortField === 'ctc'
                ? sortDir === 'asc'
                  ? 'Package: Low → High'
                  : 'Package: High → Low'
                : 'Sort by Package'
            }
          >
            <IndianRupee size={13} />
            Package
            {sortField === 'ctc' ? (
              sortDir === 'asc' ? (
                <TrendingUp size={13} />
              ) : (
                <TrendingDown size={13} />
              )
            ) : (
              <SortIcon field="ctc" />
            )}
          </button>
          <button
            className={`jb-sort-btn ${sortField === 'applyBy' ? 'active' : ''}`}
            onClick={() => handleSort('applyBy')}
          >
            <Clock size={13} />
            Deadline
            <SortIcon field="applyBy" />
          </button>
          <button
            className={`jb-sort-btn ${sortField === 'company' ? 'active' : ''}`}
            onClick={() => handleSort('company')}
          >
            <Building2 size={13} />
            Company
            <SortIcon field="company" />
          </button>
        </div>
      </div>

      {/* Showing info */}
      <p className="jb-showing">
        {searchTerm
          ? `Showing ${paginated.length} of ${processed.length} results for "${searchTerm}"`
          : `Showing ${paginated.length} of ${processed.length} ${statusFilter.toLowerCase()} jobs`}
      </p>

      {/* ── Table Card ── */}
      <div className="jb-card">
        <div className="jb-table-wrap">
          <table className="jb-table">
            <thead>
              <tr>
                <th className="jb-th-num">#</th>
                <th
                  className="jb-th-sort"
                  onClick={() => handleSort('company')}
                >
                  Company <SortIcon field="company" />
                </th>
                <th>Role</th>
                <th
                  className="jb-th-sort"
                  onClick={() => handleSort('applyBy')}
                >
                  Apply By <SortIcon field="applyBy" />
                </th>
                <th>Type</th>
                <th
                  className="jb-th-sort"
                  onClick={() => handleSort('ctc')}
                >
                  CTC / Stipend <SortIcon field="ctc" />
                </th>
                <th>Status</th>
                <th
                  className="jb-th-sort"
                  onClick={() => handleSort('dateOfVisit')}
                >
                  Visit Date <SortIcon field="dateOfVisit" />
                </th>
                <th className="jb-th-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan="9" className="jb-empty">
                    <Briefcase size={40} className="jb-empty-icon" />
                    <p className="jb-empty-title">
                      {searchTerm
                        ? `No jobs match "${searchTerm}"`
                        : `No ${statusFilter.toLowerCase()} jobs found`}
                    </p>
                    <p className="jb-empty-sub">Try adjusting your filters or search</p>
                  </td>
                </tr>
              ) : (
                paginated.map((job, idx) => {
                  const status = getJobStatus(job.applyByDate, job.applyByTime);
                  return (
                    <tr
                      key={job._id}
                      className="jb-row"
                      onClick={() => openModal(job)}
                    >
                      <td className="jb-td-num">
                        {(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}
                      </td>
                      <td className="jb-td-company">
                        <div className="jb-company-avatar">
                          {(job.companyName || '?')[0].toUpperCase()}
                        </div>
                        <span className="jb-company-name">{job.companyName}</span>
                      </td>
                      <td className="jb-td-role">{job.role}</td>
                      <td className="jb-td-secondary">
                        {formatApplyBy(job.applyByDate, job.applyByTime)}
                      </td>
                      <td>
                        <span className="jb-type-badge">{job.typeOfWork || 'N/A'}</span>
                      </td>
                      <td className="jb-td-ctc">
                        {job.ctc ? (
                          <span className="jb-ctc-badge">{job.ctc}</span>
                        ) : (
                          <span className="jb-td-dim">N/A</span>
                        )}
                      </td>
                      <td>
                        <span className={`jb-status jb-status-${status.toLowerCase()}`}>
                          {status}
                        </span>
                      </td>
                      <td className="jb-td-secondary">{formatDate(job.dateOfVisit)}</td>
                      <td className="jb-td-right">
                        <button
                          className="jb-view-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            openModal(job);
                          }}
                        >
                          <ExternalLink size={13} /> Details
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="jb-pagination">
            <span className="jb-page-info">
              Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
            </span>
            <div className="jb-page-controls">
              <button
                className="jb-page-btn nav"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={14} />
              </button>
              {getPageNums().map((p) => (
                <button
                  key={p}
                  className={`jb-page-btn ${currentPage === p ? 'active' : ''}`}
                  onClick={() => setCurrentPage(p)}
                >
                  {p}
                </button>
              ))}
              <button
                className="jb-page-btn nav"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Modal ── */}
      {selectedJob && (
        <Modal
          isOpen={modalIsOpen}
          onRequestClose={closeModal}
          contentLabel="Job Details"
          className="jb-modal"
          overlayClassName="jb-overlay"
        >
          <div className="jb-modal-header">
            <div className="jb-modal-header-left">
  <div className="jb-modal-avatar">
    {(selectedJob.companyName || '?')[0].toUpperCase()}
  </div>
  <div style={{ minWidth: 0, flex: 1 }}>
    <h2 className="jb-modal-title">{selectedJob.role}</h2>
    <p className="jb-modal-company">
      <Building2 size={12} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
      {selectedJob.companyName}
      {selectedJob.typeOfWork && (
        <>
          <span style={{ margin: '0 8px', color: 'var(--jb-text-muted)' }}>•</span>
          {selectedJob.typeOfWork}
        </>
      )}
      {selectedJob.ctc && (
        <>
          <span style={{ margin: '0 8px', color: 'var(--jb-text-muted)' }}>•</span>
          <strong style={{ color: 'var(--jb-gold-deep)' }}>{selectedJob.ctc}</strong>
        </>
      )}
    </p>
  </div>
</div>
            <button onClick={closeModal} className="jb-modal-close">
              <X size={20} />
            </button>
          </div>
          <div className="jb-modal-body">
            <JobPostingPreview formData={selectedJob} />
          </div>
          <div className="jb-modal-footer">
            <button className="jb-modal-secondary" onClick={closeModal}>
              Close
            </button>
            <button
              className="jb-modal-primary"
              onClick={() => handleViewStudents(selectedJob._id, selectedJob.role)}
            >
              <Users size={15} /> View Applicants
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Jobs;